import type { ChainResponse } from '../alerts-v2-service/types'
import type { AssetsResponse } from '../assets-v2-service/types'
import type { ExchangeResponse, OrderSide } from '../types'

export interface DexPoolResponse {
  address: string
  symbol_name: string
  native_symbol_name?: string | null
  chain_name: string
  dex_name: string
  cex_name: string
  dex_decimals?: number | null
  cex_decimals?: number | null
  reversed: boolean // default false
  available: number // default 1
}

export interface DexPriceResponse {
  price_usdt: number
  price_native: number
  dex: string
  chain: string
  vol24h: number | null
  liquidity_usd: number | null
}

export interface GtDsPriceResponse {
  price_usdt?: number | null
  vol24h?: number | null
}

export interface GmPriceResponse {
  price_usdt?: number | null
  pool?: string
}

export interface DexPoolPriceResponse {
  ds: GtDsPriceResponse
  gt: GtDsPriceResponse
  gm: GmPriceResponse
}

export interface DexContractResponse {
  exchange: ExchangeResponse
  asset: AssetsResponse
  chain: ChainResponse
  contract: string
}

export interface DexAssetPoolResponse {
  asset: string
  dex: string
  token: string
  primary: boolean
}

export interface GmgnPriceResponse {
  price: number
  ts: number
  vol24h: number | null
  vol1h: number | null
  quote_amount: number | null
  pool: string
  side?: OrderSide
}

export interface CexTickerResponse {
  price: number | null
  ask: number | null
  bid: number | null
  vol24h: number | null
}

export type CexOhlcvResponse = [number, number, number, number, number]

// --- defi-watcher interfaces ---

export interface DefiPoolReserves {
  base: string
  quote: string
  price: string
  ts: string
  dex: string
  base_dec?: string
  quote_dec?: string
}

export interface DefiReserveUpdateMsg {
  event: 'reserve_update'
  pool: string
  base: string
  quote: string
  price: string
  ts: number
  dex: string
  base_dec?: number
  quote_dec?: number
}

export interface DefiExecPrice {
  buy_10: number
  sell_10: number
  buy_100: number
  sell_100: number
  buy_200: number
  sell_200: number
  buy_400: number
  sell_400: number
  buy_500: number
  sell_500: number
  buy_700: number
  sell_700: number
  buy_1000: number
  sell_1000: number
  asset: string
  quote: string
  note: 'v2_exact' | 'clmm_exact' | 'spot_approx'
  ts: number
  tvl?: number
}

export interface DefiStateUpdateMsg {
  pool: string
  ts: number
  dex: string
  chain_price?: number | null
  exec_price: DefiExecPrice | null
  base_dec?: number
  quote_dec?: number
  state?: Record<string, unknown>
}

export type DefiPoolData = DefiPoolReserves | DefiReserveUpdateMsg | DefiStateUpdateMsg

// --- new concise websocket channels ---

export interface DexPriceByVolMsg {
  buy: number
  sell: number
  ts: number
}

export interface DexTvlMsg {
  tvl: number
  ts: number
}

export interface DexPoolLiveData {
  pool: string
  dex: string
  priceBuy: number | null
  priceSell: number | null
  tvl: number | null
  ts: number | null
}

// --- per-parser raw_state interfaces ---

export interface BondingCurveState {
  virtual_token_reserves: string
  virtual_sol_reserves: string
  base_dec: number
  quote_dec: number
  raw_account_hex: string
}

export interface PumpSwapState {
  base: string
  quote: string
  base_dec: number
  quote_dec: number
  raw_vault_hex: string
}

export interface AmmV4State {
  coin: string
  pc: string
  coin_dec: number
  pc_dec: number
  raw_vault_hex: string
}

export interface CpmmState {
  v0: string
  v1: string
  mint0_dec: number
  mint1_dec: number
  raw_vault_hex: string
}

export interface DammV1State {
  reserve_a: string
  reserve_b: string
  dec_a: number
  dec_b: number
  raw_vault_hex: string
}

export interface DammV2State {
  reserve_a: string
  reserve_b: string
  dec_a: number
  dec_b: number
  raw_vault_hex: string
}

export interface DlmmBin {
  amount_x: string
  amount_y: string
  price: string
  liquidity_supply: string
}

export interface DlmmState {
  active_id: number
  bin_step: number
  dec_x: number
  dec_y: number
  mint_x?: string
  mint_y?: string
  bins: DlmmBin[]
  raw_account_hex: string
}

export interface WhirlpoolTick {
  initialized: boolean
  liquidity_net: string
  liquidity_gross: string
}

export interface WhirlpoolState {
  sqrt_price: string
  dec_a: number
  dec_b: number
  liquidity?: string
  tick?: number
  tick_spacing?: number
  ticks: WhirlpoolTick[]
  raw_account_hex: string
  mint_x?: string
  mint_y?: string
}

export interface ClmmState {
  sqrt_price: string
  dec0: number
  dec1: number
  liquidity: string
  tick_current: number
  tick_spacing: number
  raw_account_hex: string
  mint_x?: string
  mint_y?: string
}

export interface PancakeV2State {
  reserve0: string
  reserve1: string
  block_timestamp_last: string
  token0: string
  token1: string
  dec0: number
  dec1: number
  raw_reserves_hex: string
}

export interface PancakeV3State {
  sqrt_price_x96: string
  liquidity: string
  tick: number
  observation_index: number
  observation_cardinality: number
  observation_cardinality_next: number
  fee_protocol: number
  unlocked: boolean
  token0: string
  token1: string
  dec0: number
  dec1: number
  raw_slot0_hex: string
  raw_liquidity_hex: string
}

export interface DexScreenerPoolItem {
  chain_id: string
  dex_id: string
  pair_address: string
  base_token_address?: string | null
  base_token_symbol?: string | null
  quote_token_address?: string | null
  quote_token_symbol?: string | null
  price_usd?: string | null
  liquidity_usd?: number | null
  vol24h?: number | null
}

export interface GeckoTerminalPoolItem {
  pool_address: string
  dex_id?: string | null
  chain_id: string
  base_token_address?: string | null
  quote_token_address?: string | null
  base_token_price_usd?: string | null
  reserve_in_usd?: number | null
  vol24h?: number | null
}

export type DexPoolChain = 'sol' | 'bsc' | 'eth' | 'base' | 'monad'

export const DEX_SCREENER_CHAIN_MAP: Record<DexPoolChain, string> = {
  sol: 'solana',
  bsc: 'bsc',
  eth: 'ethereum',
  base: 'base',
  monad: 'monad',
}

export const GECKO_TERMINAL_CHAIN_MAP: Record<DexPoolChain, string> = {
  sol: 'solana',
  bsc: 'bsc',
  eth: 'eth',
  base: 'base',
  monad: 'monad',
}
